import React from 'react';
import { Icon } from '@iconify/react';
import { useIconConfig } from '../contexts/IconConfigContext.jsx';

// ─── Tabler ───────────────────────────────────────────────────────────────────
import tablerCircle    from '@iconify-icons/tabler/circle';
import tablerDiamond   from '@iconify-icons/tabler/diamond';
import tablerFlower    from '@iconify-icons/tabler/flower';
import tablerGlass     from '@iconify-icons/tabler/glass-full';
import tablerMail      from '@iconify-icons/tabler/mail-heart';
import tablerCandle    from '@iconify-icons/tabler/candle';
import tablerGift      from '@iconify-icons/tabler/gift';
import tablerCalendar  from '@iconify-icons/tabler/calendar-heart';
import tablerChurch    from '@iconify-icons/tabler/building-church';

// ─── MDI ──────────────────────────────────────────────────────────────────────
import mdiRing         from '@iconify-icons/mdi/ring';
import mdiCircle       from '@iconify-icons/mdi/circle-outline';
import mdiFlower       from '@iconify-icons/mdi/flower-outline';
import mdiGlass        from '@iconify-icons/mdi/glass-wine';
import mdiMail         from '@iconify-icons/mdi/email-heart-outline';
import mdiCandle       from '@iconify-icons/mdi/candle';
import mdiGift         from '@iconify-icons/mdi/gift-outline';
import mdiCalendar     from '@iconify-icons/mdi/calendar-heart';
import mdiChurch       from '@iconify-icons/mdi/church-outline';

// ─── Phosphor (3 weights share same structure, different suffix) ──────────────
// thin
import phCircleThin    from '@iconify-icons/ph/circle-thin';
import phDiamondThin   from '@iconify-icons/ph/diamond-thin';
import phFlowerThin    from '@iconify-icons/ph/flower-tulip-thin';
import phGlassThin     from '@iconify-icons/ph/champagne-thin';
import phMailThin      from '@iconify-icons/ph/envelope-thin';
import phCandleThin    from '@iconify-icons/ph/flame-thin';
import phGiftThin      from '@iconify-icons/ph/gift-thin';
import phCalendarThin  from '@iconify-icons/ph/calendar-thin';
import phChurchThin    from '@iconify-icons/ph/church-thin';
// regular
import phCircle        from '@iconify-icons/ph/circle';
import phDiamond       from '@iconify-icons/ph/diamond';
import phFlower        from '@iconify-icons/ph/flower-tulip';
import phGlass         from '@iconify-icons/ph/champagne';
import phMail          from '@iconify-icons/ph/envelope';
import phCandle        from '@iconify-icons/ph/flame';
import phGift          from '@iconify-icons/ph/gift';
import phCalendar      from '@iconify-icons/ph/calendar';
import phChurch        from '@iconify-icons/ph/church';
// duotone
import phCircleDuo     from '@iconify-icons/ph/circle-duotone';
import phDiamondDuo    from '@iconify-icons/ph/diamond-duotone';
import phFlowerDuo     from '@iconify-icons/ph/flower-lotus-duotone';
import phGlassDuo      from '@iconify-icons/ph/champagne-duotone';
import phMailDuo       from '@iconify-icons/ph/envelope-duotone';
import phCandleDuo     from '@iconify-icons/ph/flame-duotone';
import phGiftDuo       from '@iconify-icons/ph/gift-duotone';
import phCalendarDuo   from '@iconify-icons/ph/calendar-duotone';
import phChurchDuo     from '@iconify-icons/ph/church-duotone';

// ─── Game Icons ───────────────────────────────────────────────────────────────
import gameLinkedRings from '@iconify-icons/game-icons/linked-rings';
import gameRose        from '@iconify-icons/game-icons/rose';
import gameChampagne   from '@iconify-icons/game-icons/champagne-cork';
import gameHearts      from '@iconify-icons/game-icons/hearts';
import gameCandles     from '@iconify-icons/game-icons/candles';
import gameRingBox     from '@iconify-icons/game-icons/ring-box';
import gameDove        from '@iconify-icons/game-icons/dove';
import gameChurch      from '@iconify-icons/game-icons/church';

// ─── Noto (full-colour emoji — ignores CSS colour) ───────────────────────────
import notoRing        from '@iconify-icons/noto/ring';
import notoRose        from '@iconify-icons/noto/rose';
import notoGlass       from '@iconify-icons/noto/clinking-glasses';
import notoMail        from '@iconify-icons/noto/heart-with-ribbon';
import notoCandle      from '@iconify-icons/noto/candle';
import notoGift        from '@iconify-icons/noto/wrapped-gift';
import notoBouquet     from '@iconify-icons/noto/bouquet';
import notoChurch      from '@iconify-icons/noto/church';

// ─── Library → icon map ───────────────────────────────────────────────────────
const ICON_MAP = {
  tabler:  { rings: { ring: tablerCircle,  diamond: tablerDiamond  }, flower: tablerFlower, glass: tablerGlass, mailHeart: tablerMail, candle: tablerCandle, gift: tablerGift, calendar: tablerCalendar, church: tablerChurch },
  mdi:     { rings: { ring: mdiRing,       diamond: null, plain: mdiCircle }, flower: mdiFlower, glass: mdiGlass, mailHeart: mdiMail, candle: mdiCandle, gift: mdiGift, calendar: mdiCalendar, church: mdiChurch },
  'ph-thin':{ rings: { ring: phCircleThin,  diamond: phDiamondThin  }, flower: phFlowerThin, glass: phGlassThin, mailHeart: phMailThin, candle: phCandleThin, gift: phGiftThin, calendar: phCalendarThin, church: phChurchThin },
  ph:      { rings: { ring: phCircle,       diamond: phDiamond      }, flower: phFlower, glass: phGlass, mailHeart: phMail, candle: phCandle, gift: phGift, calendar: phCalendar, church: phChurch },
  'ph-duo':{ rings: { ring: phCircleDuo,    diamond: phDiamondDuo   }, flower: phFlowerDuo, glass: phGlassDuo, mailHeart: phMailDuo, candle: phCandleDuo, gift: phGiftDuo, calendar: phCalendarDuo, church: phChurchDuo },
  game:    { rings: { single: gameLinkedRings }, flower: gameRose, glass: gameChampagne, mailHeart: gameHearts, candle: gameCandles, gift: gameRingBox, calendar: gameDove, church: gameChurch },
  noto:    { rings: { pair: notoRing }, flower: notoRose, glass: notoGlass, mailHeart: notoMail, candle: notoCandle, gift: notoGift, calendar: notoBouquet, church: notoChurch },
};

// ─── Hero rings compositor ────────────────────────────────────────────────────
function TwoRings({ size, color, ringsConfig }) {
  // Game Icons: single pre-composed linked-rings icon
  if (ringsConfig.single) {
    return (
      <div style={{ color, lineHeight: 0 }}>
        <Icon icon={ringsConfig.single} width={size * 1.4} height={size * 1.4} />
      </div>
    );
  }

  // Noto: two ring emojis side-by-side (emoji colour, no tint)
  if (ringsConfig.pair) {
    const w = size * 1.55;
    return (
      <div style={{ position: 'relative', width: w, height: size, lineHeight: 0 }}>
        <Icon icon={ringsConfig.pair} width={size} height={size} style={{ position: 'absolute', left: 0, top: 0 }} />
        <Icon icon={ringsConfig.pair} width={size} height={size} style={{ position: 'absolute', left: size * 0.55, top: 0 }} />
      </div>
    );
  }

  // MDI: diamond ring + plain circle band
  // mdiRing's circle occupies ~65% of its bounding box (stone sits on top),
  // so scale mdiCircle to match and shift it to align with the ring portion.
  if (ringsConfig.plain) {
    const bandSize = size * 0.65;
    const bandTop  = size * 0.28;
    return (
      <div style={{ position: 'relative', width: size * 1.5, height: size, color, lineHeight: 0 }}>
        <Icon icon={ringsConfig.ring}  width={size}     height={size}     style={{ position: 'absolute', left: 0,            top: 0       }} />
        <Icon icon={ringsConfig.plain} width={bandSize} height={bandSize} style={{ position: 'absolute', left: size * 0.52, top: bandTop }} />
      </div>
    );
  }

  // Default (Tabler / Phosphor): two circles + small diamond accent on left
  const diamondSize = size * 0.34;
  return (
    <div style={{ position: 'relative', width: size * 1.55, height: size * 1.15, color, lineHeight: 0 }}>
      <Icon icon={ringsConfig.ring} width={size} height={size} style={{ position: 'absolute', left: 0, top: size * 0.15 }} />
      <Icon icon={ringsConfig.ring} width={size} height={size} style={{ position: 'absolute', left: size * 0.62, top: size * 0.15 }} />
      {ringsConfig.diamond && (
        <Icon icon={ringsConfig.diamond} width={diamondSize} height={diamondSize}
          style={{ position: 'absolute', left: size * 0.5 - diamondSize / 2, top: 0 }} />
      )}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
const HERO_RINGS = [
  { x: '3%',  y: '10%', size: 32, rot: -10 },
  { x: '87%', y: '16%', size: 30, rot: 14  },
  { x: '4%',  y: '60%', size: 34, rot: 8   },
  { x: '86%', y: '68%', size: 32, rot: -16 },
  { x: '45%', y: '3%',  size: 28, rot: 6   },
  { x: '47%', y: '94%', size: 30, rot: -8  },
];

const SUPPORTING_SLOTS = [
  { x: '12%', y: '4%',  size: 26, rot: 18,  icon: 'flower'    },
  { x: '78%', y: '5%',  size: 24, rot: -22, icon: 'candle'    },  // was glass
  { x: '20%', y: '24%', size: 24, rot: 12,  icon: 'mailHeart' },
  { x: '72%', y: '30%', size: 26, rot: -10, icon: 'candle'    },
  { x: '14%', y: '38%', size: 26, rot: 8,   icon: 'flower'    },  // was church
  { x: '82%', y: '46%', size: 26, rot: -18, icon: 'calendar'  },  // was gift
  { x: '18%', y: '78%', size: 24, rot: 8,   icon: 'calendar'  },
  { x: '76%', y: '84%', size: 26, rot: -26, icon: 'mailHeart' },  // was church
  { x: '30%', y: '14%', size: 22, rot: 35,  icon: 'flower'    },  // was glass
  { x: '66%', y: '13%', size: 24, rot: -8,  icon: 'candle'    },
  { x: '8%',  y: '88%', size: 24, rot: 20,  icon: 'mailHeart' },
  { x: '90%', y: '87%', size: 24, rot: -14, icon: 'flower'    },  // was gift
  { x: '36%', y: '50%', size: 22, rot: 22,  icon: 'candle'    },  // was flower dupe → candle
  { x: '60%', y: '57%', size: 22, rot: -18, icon: 'calendar'  },
];

// ─── Main component ───────────────────────────────────────────────────────────
export function WeddingBandsDecor({ isDarkMode }) {
  const { config } = useIconConfig();
  const { library, enabledIcons, opacity } = config;
  const color = isDarkMode ? '#c9a84c' : '#b8962e';
  const lib = ICON_MAP[library] || ICON_MAP.mdi;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {enabledIcons.has('rings') && HERO_RINGS.map((r, i) => (
        <div key={`r-${i}`} style={{ position: 'absolute', left: r.x, top: r.y, transform: `rotate(${r.rot}deg)`, opacity, lineHeight: 0 }}>
          <TwoRings size={r.size} color={color} ringsConfig={lib.rings} />
        </div>
      ))}

      {SUPPORTING_SLOTS.map((slot, i) => {
        if (!enabledIcons.has(slot.icon)) return null;
        const iconData = lib[slot.icon];
        if (!iconData) return null;
        return (
          <div key={`s-${i}`} style={{ position: 'absolute', left: slot.x, top: slot.y, transform: `rotate(${slot.rot}deg)`, opacity, color, lineHeight: 0 }}>
            <Icon icon={iconData} width={slot.size} height={slot.size} />
          </div>
        );
      })}
    </div>
  );
}
